import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@ui-lib/components';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import {
  CreateServiceCenterRequest,
  SearchServiceCentersRequest,
  SearchServiceCentersResponse,
  ServiceCenterClient,
  UpdateServiceCenterRequest,
} from '../../core/openapi/bff-admin-api';
import { LoadingState } from '../../shared/types/loading.type';
import { outputError } from '../../shared/utils/output-error.function';
import { prepareRequest } from '../../shared/utils/prepare-request.function';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/const/pagination.constants';

@Injectable()
export class ServiceCentersService {
  private readonly serviceCenterClient = inject(ServiceCenterClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private readonly _serviceCentersLoadingStatus: WritableSignal<LoadingState> =
    signal<LoadingState>(LoadingState.IDLE);
  private readonly _actionLoadingStatus: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );
  private readonly _totalCount: WritableSignal<number> = signal<number>(0);

  public readonly serviceCentersLoadingStatus: Signal<LoadingState> =
    this._serviceCentersLoadingStatus.asReadonly();
  public readonly actionLoadingStatus: Signal<LoadingState> =
    this._actionLoadingStatus.asReadonly();
  public readonly totalCount: Signal<number> = this._totalCount.asReadonly();

  private readonly serviceCentersRequest$ = new BehaviorSubject<SearchServiceCentersRequest>({
    skip: 0,
    take: DEFAULT_TABLE_PAGE_SIZE,
    includeTotalCount: true,
  });

  public readonly serviceCenters$: Observable<SearchServiceCentersResponse[]> =
    this.serviceCentersRequest$.pipe(
      tap(() => this._serviceCentersLoadingStatus.set(LoadingState.LOADING)),
      switchMap((request) =>
        this.serviceCenterClient.v1ServiceCenterSearchPost(request).pipe(
          catchError((err) => {
            this.toastService.error({
              message: this.translateService.instant(
                'monitoring.service-centers.toast.errors.list-fetch-failed',
                { error: outputError(err, this.translateService.instant('errors.unknown')) },
              ),
            });

            this._totalCount.set(0);
            return of({ items: [], totalCount: 0 });
          }),
        ),
      ),
      tap(({ totalCount }) => {
        this._totalCount.set(totalCount ?? 0);
        this._serviceCentersLoadingStatus.set(LoadingState.READY);
      }),
      map(({ items }) => items ?? []),
    );

  public updateServiceCentersRequest(params: Partial<SearchServiceCentersRequest>): void {
    this.serviceCentersRequest$.next({
      ...this.serviceCentersRequest$.value,
      ...prepareRequest(params),
    });
  }

  public reloadServiceCenters(): void {
    this.serviceCentersRequest$.next({
      ...this.serviceCentersRequest$.value,
    });
  }

  public createServiceCenter(request: CreateServiceCenterRequest): Observable<boolean> {
    this._actionLoadingStatus.set(LoadingState.LOADING);

    return this.serviceCenterClient.v1ServiceCenterPost(request).pipe(
      map(() => true),
      catchError((err) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.service-centers.toast.errors.create-failed',
            { error: outputError(err, this.translateService.instant('errors.unknown')) },
          ),
        });

        return of(false);
      }),
      finalize(() => this._actionLoadingStatus.set(LoadingState.READY)),
    );
  }

  public updateServiceCenter(request: UpdateServiceCenterRequest): Observable<boolean> {
    this._actionLoadingStatus.set(LoadingState.LOADING);

    return this.serviceCenterClient.v1ServiceCenterPut(request).pipe(
      map(() => true),
      catchError((err) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.service-centers.toast.errors.update-failed',
            { error: outputError(err, this.translateService.instant('errors.unknown')) },
          ),
        });

        return of(false);
      }),
      finalize(() => this._actionLoadingStatus.set(LoadingState.READY)),
    );
  }

  public deleteServiceCenter(id: string): Observable<boolean> {
    this._actionLoadingStatus.set(LoadingState.LOADING);

    return this.serviceCenterClient.v1ServiceCenterIdDelete(id).pipe(
      map(() => true),
      catchError((err) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.service-centers.toast.errors.delete-failed',
            { error: outputError(err, this.translateService.instant('errors.unknown')) },
          ),
        });

        return of(false);
      }),
      finalize(() => this._actionLoadingStatus.set(LoadingState.READY)),
    );
  }
}
